import { ButtonProps } from '@atlaskit/button';
import { SelectableButton } from '@databases/atlaskit/SelectableButton';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  BacklinkDisplayType,
  Database,
  DatabaseFieldType,
} from '@databases/types/database';
import { DatabaseCalculationType } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import {
  isEmptyValue,
  getStringRepresentation,
} from '@databases/utils/database/field-types';
import {
  getDatabaseFieldRequired,
  getDatabaseEntryRequired,
  getDatabaseValue,
  getNumberRepresentationOfBacklink,
} from '@databases/utils/database/utils';
import { SimpleStore } from '@databases/utils/redux/redux';
import React, { forwardRef } from 'react';
import styled from 'styled-components';

import { CalculationSelectOption } from './types';

export const isEmpty = (
  database: Database,
  fieldId: string,
  entryId: string
): boolean => {
  const field = getDatabaseFieldRequired(database, fieldId);
  const store = defaultStoreUtils.useStore();
  const state = store.getState();

  const entry = getDatabaseEntryRequired(database, entryId);
  const value = getDatabaseValue(database, field, entry);
  if (
    field.type === DatabaseFieldType.BACKLINK ||
    field.type === DatabaseFieldType.ENTRY_DETAILS
  ) {
    const stringRepresentation = getStringRepresentation(
      { database, field, entry, value },
      state
    );
    const numberRepresentation = getNumberRepresentationOfBacklink(
      database,
      field,
      entry,
      value,
      state
    );
    if (stringRepresentation === '') {
      // If the string representation is '', it could be two things:
      // 1. the cell is empty
      // 2. the cell references an unnamed entry
      // In the case of 1, the number representation will be 0.
      return Number(numberRepresentation) === 0;
    } else {
      return !stringRepresentation;
    }
  } else if (field.type === value.type) {
    return isEmptyValue({ database, field, entry, value }, state);
  } else {
    // The entry does not always equal the field type (users can change the
    // field type). In this case, it is possible the value can be converted to
    // the field type and displayed. If this is the case, it is not empty. To
    // determine if it has a display value, we get the string representation.
    return !getStringRepresentation({ database, field, entry, value }, state);
  }
};

export const getUniqueIdentifiers = (
  database: Database,
  fieldId: string,
  entryId: string,
  store: SimpleStore<DefaultRootState>
): string[] => {
  const field = getDatabaseFieldRequired(database, fieldId);
  const entry = getDatabaseEntryRequired(database, entryId);
  const value = getDatabaseValue(database, field, entry);
  const state = store.getState();

  const stringRepresentation = getStringRepresentation(
    { database, field, entry, value },
    state
  );

  if (stringRepresentation === undefined || stringRepresentation.trim() === '') {
    return [];
  }

  if (field.type === DatabaseFieldType.TEXT) {
    return [stringRepresentation];
  }

  if (
    field.type === DatabaseFieldType.ENTRY_LINK &&
    field.settings.contentId === undefined
  ) {
    return [];
  }

  if (
    field.type === DatabaseFieldType.BACKLINK &&
    field.settings.backlinkDisplayType !== BacklinkDisplayType.NUMBER
  ) {
    const numberRepresentation = getNumberRepresentationOfBacklink(
      database,
      field,
      entry,
      value,
      state
    );
    // if the titles are unnamed, use the entry id as the unique identifier
    if (
      stringRepresentation?.trim() === '' &&
      numberRepresentation &&
      !isNaN(Number(numberRepresentation))
    ) {
      return Array(Number(numberRepresentation)).fill(entry.id);
    }
  }

  return stringRepresentation?.trim().split('\n') || [];
};

export const numericOptions = [
  DatabaseCalculationType.SUM,
  DatabaseCalculationType.AVERAGE,
  DatabaseCalculationType.MIN,
  DatabaseCalculationType.MAX,
  DatabaseCalculationType.COUNT,
  DatabaseCalculationType.COUNT_UNIQUE,
  DatabaseCalculationType.COUNT_EMPTY,
  DatabaseCalculationType.PERCENT_EMPTY,
  DatabaseCalculationType.PERCENT_NOT_EMPTY,
];

export const nonNumericOptions = [
  DatabaseCalculationType.COUNT,
  DatabaseCalculationType.COUNT_UNIQUE,
  DatabaseCalculationType.COUNT_EMPTY,
  DatabaseCalculationType.PERCENT_EMPTY,
  DatabaseCalculationType.PERCENT_NOT_EMPTY,
];

export const getCalculationOptionsForFieldType = (
  fieldType: DatabaseFieldType
): DatabaseCalculationType[] => {
  switch (fieldType) {
    case DatabaseFieldType.NUMBER:
      return numericOptions;
    case DatabaseFieldType.ATTACHMENT:
      return nonNumericOptions.filter(
        (calculation) => calculation !== DatabaseCalculationType.COUNT_UNIQUE
      );
    default:
      return nonNumericOptions;
  }
};

export const handleCalculationSelectionChange = (
  option: CalculationSelectOption | null,
  databaseContentId: string,
  dispatch: ReturnType<typeof defaultStoreUtils.useDispatch>,
  fieldId: string,
  viewId: string
) => {
  if (option) {
    dispatch(
      addChangeToDraft(databaseContentId, 'main', {
        type: DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS,
        viewId: viewId,
        fieldId: fieldId,
        value: option.value,
      })
    );
  } else {
    dispatch(
      addChangeToDraft(databaseContentId, 'main', {
        type: DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION,
        viewId: viewId,
        fieldId: fieldId,
      })
    );
  }
};

export const isPercentCalculation = (calculationType: DatabaseCalculationType) => {
  return (
    calculationType === DatabaseCalculationType.PERCENT_EMPTY ||
    calculationType === DatabaseCalculationType.PERCENT_NOT_EMPTY
  );
};

// Adds a percent sign to the end of the result if the calculation type is a percent calculation.
export const formatPercent = (
  calculationType: DatabaseCalculationType,
  result: string
) => {
  if (isPercentCalculation(calculationType) && !result.endsWith('%')) {
    return `${result}%`;
  } else {
    return result;
  }
};

const Container = styled.div`
  position: relative;
`;

type TriggerButtonProps = ButtonProps & {
  isActive?: boolean;
};

export const TriggerButton = forwardRef<HTMLDivElement, TriggerButtonProps>(
  function TriggerButton({ isActive, ...buttonProps }, ref) {
    return (
      <Container ref={ref}>
        <SelectableButton
          {...buttonProps}
          $isActive={isActive}
          $isSelected={buttonProps.isSelected}
          appearance="subtle"
        >
          {buttonProps.children}
        </SelectableButton>
      </Container>
    );
  }
);
